package web.fstech.onthecoffee

import kotlinx.browser.window
import kotlinx.coroutines.await
import org.w3c.fetch.Headers
import org.w3c.fetch.RequestInit
import kotlin.js.ExperimentalWasmJsInterop
import kotlin.js.toJsString

@OptIn(ExperimentalWasmJsInterop::class)
object SupabaseService {
    private const val SUPABASE_URL = "https://rvqdxxtmjazddxkwapff.supabase.co"
    private const val SUPABASE_ANON_KEY = "yeyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJ2cWR4eHRtamF6ZGR4a3dhcGZmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTAwNTkzNzksImV4cCI6MjEwNTYzNTM3OX0.8hbRfLBneop01q4mgDo7O24I54zfgMGO2k83n3Dr5aY"
    private const val TOKEN_KEY = "sb_access_token"

    fun getAccessToken(): String? {
        val token = window.localStorage.getItem(TOKEN_KEY)
        return if (token.isNullOrBlank()) null else token
    }

    fun isSignedIn(): Boolean {
        return !getAccessToken().isNullOrBlank()
    }

    fun saveAccessToken(token: String) {
        window.localStorage.setItem(TOKEN_KEY, token)
    }

    fun clearSession() {
        window.localStorage.removeItem(TOKEN_KEY)
    }

    suspend fun signUp(email: String, pass: String): Result<Unit> {
        return try {
            val bodyStr = "{\"email\":\"$email\",\"password\":\"$pass\"}"
            val response = window.fetch(
                "$SUPABASE_URL/auth/v1/signup",
                RequestInit(
                    method = "POST",
                    headers = createHeaders(),
                    body = bodyStr.toJsString()
                )
            ).await()
            if (response.ok) {
                Result.success(Unit)
            } else {
                Result.failure(Exception("Sign up failed: ${response.statusText}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun signIn(email: String, pass: String): Result<Unit> {
        return try {
            val bodyStr = "{\"email\":\"$email\",\"password\":\"$pass\"}"
            val response = window.fetch(
                "$SUPABASE_URL/auth/v1/token?grant_type=password",
                RequestInit(
                    method = "POST",
                    headers = createHeaders(),
                    body = bodyStr.toJsString()
                )
            ).await()
            if (response.ok) {
                val jsonText = response.text().await().toString()
                val token = extractAccessToken(jsonText)
                if (!token.isNullOrBlank()) {
                    saveAccessToken(token)
                }
                Result.success(Unit)
            } else {
                Result.failure(Exception("Sign in failed: ${response.statusText}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun forgotPassword(email: String): Result<Unit> {
        return try {
            val bodyStr = "{\"email\":\"$email\"}"
            val response = window.fetch(
                "$SUPABASE_URL/auth/v1/recover",
                RequestInit(
                    method = "POST",
                    headers = createHeaders(),
                    body = bodyStr.toJsString()
                )
            ).await()
            if (response.ok) {
                Result.success(Unit)
            } else {
                Result.failure(Exception("Password recovery failed: ${response.statusText}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    suspend fun deleteAccount(): Result<Unit> {
        val token = getAccessToken()
        if (token.isNullOrBlank()) {
            return Result.failure(Exception("Not signed in"))
        }
        return try {
            val response = window.fetch(
                "$SUPABASE_URL/auth/v1/user",
                RequestInit(
                    method = "DELETE",
                    headers = createHeaders(token)
                )
            ).await()
            if (response.ok) {
                clearSession()
                Result.success(Unit)
            } else {
                Result.failure(Exception("Delete account failed: ${response.statusText}"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    private fun createHeaders(accessToken: String? = null): Headers {
        return Headers().apply {
            append("apikey", SUPABASE_ANON_KEY)
            append("Content-Type", "application/json")
            if (!accessToken.isNullOrBlank()) {
                append("Authorization", "Bearer $accessToken")
            }
        }
    }

    private fun extractAccessToken(jsonStr: String): String? {
        val key = "\"access_token\":"
        val idx = jsonStr.indexOf(key)
        if (idx == -1) return null
        val startQuote = jsonStr.indexOf('"', idx + key.length)
        if (startQuote == -1) return null
        val endQuote = jsonStr.indexOf('"', startQuote + 1)
        if (endQuote == -1) return null
        return jsonStr.substring(startQuote + 1, endQuote)
    }
}
